import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { hotTopics, topicCategories } from "@/mocks/topics";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/rank", label: "吃瓜榜单" },
  { to: "/about", label: "关于我们" },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const results = useMemo(() => {
    const k = keyword.trim();
    if (!k) return [];
    return hotTopics
      .filter((t) => t.title.includes(k) || t.tags.some((tag) => tag.includes(k)))
      .slice(0, 5);
  }, [keyword]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const k = keyword.trim();
    if (!k) return;
    setKeyword("");
    navigate(`/rank?q=${encodeURIComponent(k)}`);
  };

  const tickerItems = hotTopics.slice(0, 6);
  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-9 w-full items-center gap-3 bg-primary-950 px-4 text-xs text-background-100 md:px-8">
        <span className="hidden shrink-0 items-center gap-1 font-label font-medium text-accent-300 md:flex">
          <i className="ri-time-line h-4 w-4 flex items-center justify-center"></i>
          今日瓜 · 实时刷新
        </span>
        <div className="relative flex-1 overflow-hidden">
          <div className="flex w-max animate-gua-marquee">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center">
                {tickerItems.map((t) => (
                  <Link
                    key={`${dup}-${t.id}`}
                    to={`/topic/${t.id}`}
                    className="mr-6 whitespace-nowrap text-background-100/90 transition-colors hover:text-accent-300"
                  >
                    · {t.title}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
        <span className="hidden shrink-0 items-center gap-1 text-background-200 md:flex">
          <i className="ri-calendar-line h-4 w-4 flex items-center justify-center"></i>
          2026-09-12 更新
        </span>
      </div>

      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-fill h-6 w-6 flex items-center justify-center"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl font-black tracking-tight text-foreground-950 md:text-2xl">
              黑料瓜网
            </span>
            <span className="mt-0.5 font-label text-[10px] tracking-[0.2em] text-primary-600">
              GUA NEWS
            </span>
          </span>
        </Link>

        <nav className="ml-2 hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 font-label text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="relative">
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 font-label text-sm font-medium transition-colors ${
                catOpen
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
              aria-expanded={catOpen}
            >
              主题分类
              <i
                className={`ri-arrow-down-s-line h-4 w-4 flex items-center justify-center transition-transform ${
                  catOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
            {catOpen ? (
              <div className="absolute left-0 top-full mt-2 w-72 rounded-lg border border-background-200 bg-background-50 p-2 shadow-none">
                {topicCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-start gap-3 rounded-md px-3 py-2 transition-colors hover:bg-secondary-50"
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className={`${cat.icon} h-4 w-4 flex items-center justify-center`}></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="font-label text-sm font-medium text-foreground-950">
                        {cat.name}
                      </span>
                      <span className="mt-0.5 line-clamp-1 text-xs text-foreground-500">
                        {cat.desc}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <form
            onSubmit={submitSearch}
            className="relative hidden md:block"
            role="search"
          >
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索瓜名 / 关键词"
              className="w-44 rounded-md border border-background-300 bg-background-100 py-2 pl-9 pr-3 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 lg:w-56"
              aria-label="站内搜索"
            />
            {results.length > 0 ? (
              <div className="absolute right-0 top-full mt-2 w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {results.map((t) => (
                  <Link
                    key={t.id}
                    to={`/topic/${t.id}`}
                    onClick={() => setKeyword("")}
                    className="flex items-center gap-3 border-b border-background-100 px-3 py-2 last:border-b-0 hover:bg-background-100"
                  >
                    <i className="ri-fire-line h-4 w-4 shrink-0 text-primary-500"></i>
                    <span className="line-clamp-1 text-sm text-foreground-800">{t.title}</span>
                  </Link>
                ))}
              </div>
            ) : null}
          </form>

          <Link
            to="/rank"
            className="hidden shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:flex"
          >
            <i className="ri-list-check h-4 w-4 flex items-center justify-center"></i>
            看榜单
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} h-5 w-5 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <form onSubmit={submitSearch} className="relative mb-3" role="search">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索瓜名 / 关键词"
              className="w-full rounded-md border border-background-300 bg-background-100 py-2.5 pl-9 pr-3 font-label text-sm outline-none focus:border-primary-400"
              aria-label="站内搜索"
            />
          </form>
          <div className="flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-2 py-2.5 font-label text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <p className="mt-3 px-2 font-label text-xs font-medium text-foreground-500">主题分类</p>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {topicCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 hover:bg-secondary-50"
              >
                <i className={`${cat.icon} h-4 w-4 text-secondary-600`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}